import { useMemo } from 'react'
import DailySparkline from '@/components/gcp/DailySparkline'
import GcpCollapsible from '@/components/gcp/GcpCollapsible'
import GcpSectionErrorBanner from '@/components/gcp/GcpSectionErrorBanner'
import GcpStatCard from '@/components/gcp/GcpStatCard'
import MonthlyTrendChart from '@/components/gcp/MonthlyTrendChart'
import {
  asNumber,
  asRecordArray,
  asString,
  buildServiceSummaryRows,
  buildSpendClassification,
  computeMtdProjection,
  computeOverallMomChange,
  formatMonthLabel,
  formatPct,
  formatUsd,
  getBiggestChange,
  getLatestMonthlyTotal,
  getRunRateProjection,
  getSectionData,
  parseMonthlyByService,
  type CreditsBreakdownRow,
  type DailyAnomalyRow,
  type DailyTrendRow,
  type SpendClassification,
} from '@/components/gcp/gcpHelpers'
import type { GcpAnalysisResult } from '@/services/gcpCosts'
import { isGcpSectionError } from '@/services/gcpCosts'

interface GcpOverviewProps {
  analysis: GcpAnalysisResult
}

const CLASSIFICATION_COLORS: Record<keyof Omit<SpendClassification, 'total'>, string> = {
  steady: 'bg-slate-500',
  usage_driven: 'bg-sky-500',
  new_or_changed: 'bg-amber-500',
  tax_and_credits: 'bg-violet-500',
}

const CLASSIFICATION_LABELS: Record<keyof Omit<SpendClassification, 'total'>, string> = {
  steady: 'Steady infrastructure',
  usage_driven: 'Usage-driven',
  new_or_changed: 'New or changed',
  tax_and_credits: 'Tax & credits',
}

function scrollToSection(anchorId: string) {
  const el = document.getElementById(`gcp-section-${anchorId}`)
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function scrollToWhatChanged() {
  const el = document.getElementById('gcp-what-changed')
  el?.scrollIntoView({ behavior: 'smooth', block: 'start' })
}

function SpendClassificationBar({ classification }: { classification: SpendClassification }) {
  const total = classification.total
  if (total <= 0) return null

  const segments: Array<{
    key: keyof Omit<SpendClassification, 'total'>
    value: number
  }> = [
    { key: 'steady', value: classification.steady },
    { key: 'usage_driven', value: classification.usage_driven },
    { key: 'new_or_changed', value: classification.new_or_changed },
    { key: 'tax_and_credits', value: classification.tax_and_credits },
  ]

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between gap-2">
        <h4 className="text-sm font-medium text-gray-800">Spend classification</h4>
        <span className="text-xs text-gray-500">
          100% of {formatUsd(total)}/mo run rate
        </span>
      </div>
      <div className="flex h-3 overflow-hidden rounded-full bg-gray-100" aria-hidden="true">
        {segments.map((segment) => {
          if (segment.value <= 0) return null
          const widthPct = (segment.value / total) * 100
          return (
            <div
              key={segment.key}
              className={CLASSIFICATION_COLORS[segment.key]}
              style={{ width: `${widthPct}%` }}
            />
          )
        })}
      </div>
      <div className="flex flex-wrap gap-x-4 gap-y-1 text-xs text-gray-600">
        {segments.map((segment) => {
          if (segment.value <= 0) return null
          const pct = total > 0 ? (segment.value / total) * 100 : 0
          return (
            <span key={segment.key} className="inline-flex items-center gap-1.5">
              <span
                className={`inline-block h-2.5 w-2.5 rounded-sm ${CLASSIFICATION_COLORS[segment.key]}`}
              />
              <span>
                {CLASSIFICATION_LABELS[segment.key]}: {formatUsd(segment.value)} ({formatPct(pct)})
              </span>
            </span>
          )
        })}
      </div>
    </div>
  )
}

export default function GcpOverview({ analysis }: GcpOverviewProps) {
  const billingRaw = analysis.sections.billing
  const billingSection = getSectionData(analysis, 'billing')
  const miscSection = getSectionData(analysis, 'misc')

  const monthlyRows = useMemo(
    () => (billingSection ? parseMonthlyByService(billingSection) : []),
    [billingSection]
  )

  const { month: latestMonth, total: latestTotal } = useMemo(() => {
    const backendMonth = billingSection?.latest_complete_month
      ? asString(billingSection.latest_complete_month)
      : null
    if (backendMonth) {
      const total = monthlyRows
        .filter((row) => row.month === backendMonth)
        .reduce((sum, row) => sum + row.net_cost, 0)
      return { month: backendMonth, total }
    }
    return getLatestMonthlyTotal(monthlyRows)
  }, [billingSection, monthlyRows])

  const mtd = useMemo(() => {
    const dailyRows: DailyTrendRow[] = asRecordArray(billingSection?.daily_trend).map((row) => ({
      date: asString(row.date),
      net_cost: asNumber(row.net_cost) ?? 0,
    }))
    return computeMtdProjection(dailyRows)
  }, [billingSection])

  const mom = useMemo(() => computeOverallMomChange(monthlyRows), [monthlyRows])

  const runRateProjection = useMemo(
    () => getRunRateProjection(billingSection, mtd),
    [billingSection, mtd]
  )

  const biggestChange = useMemo(() => getBiggestChange(billingSection), [billingSection])

  const classification = useMemo(
    () => buildSpendClassification(billingSection, miscSection),
    [billingSection, miscSection]
  )

  const taxLabel = useMemo(() => {
    const taxItems = asRecordArray(miscSection?.tax_items)
    if (taxItems.length === 0) return null
    const labels = taxItems.map((row) => asString(row.label)).filter(Boolean)
    return labels.length > 0 ? labels.join(', ') : 'Sales tax'
  }, [miscSection])

  const serviceRows = useMemo(
    () => buildServiceSummaryRows(monthlyRows, latestMonth, latestTotal),
    [monthlyRows, latestMonth, latestTotal]
  )

  const credits: CreditsBreakdownRow[] = useMemo(
    () =>
      asRecordArray(billingSection?.credits_breakdown).map((row) => ({
        month: asString(row.month),
        credit_type: asString(row.credit_type),
        credit_name: asString(row.credit_name),
        credit_amount: asNumber(row.credit_amount) ?? 0,
      })),
    [billingSection]
  )

  const anomalies: DailyAnomalyRow[] = useMemo(
    () =>
      asRecordArray(billingSection?.daily_anomalies).map((row) => ({
        date: asString(row.date),
        service: asString(row.service),
        cost: asNumber(row.cost) ?? 0,
        baseline_avg: asNumber(row.baseline_avg) ?? 0,
        ratio: asNumber(row.ratio) ?? 0,
      })),
    [billingSection]
  )

  const dailyRows: DailyTrendRow[] = useMemo(
    () =>
      asRecordArray(billingSection?.daily_trend).map((row) => ({
        date: asString(row.date),
        net_cost: asNumber(row.net_cost) ?? 0,
      })),
    [billingSection]
  )

  if (isGcpSectionError(billingRaw)) {
    return (
      <GcpSectionErrorBanner
        title="Billing overview failed"
        error={billingRaw.error}
        missingRoleHint={billingRaw.missing_role_hint}
      />
    )
  }

  if (!billingSection) {
    return <p className="text-sm text-gray-500">Billing overview data is not available.</p>
  }

  const momPositive = (mom?.change ?? 0) >= 0

  return (
    <div className="space-y-4">
      <h3 className="container-title">Overview</h3>

      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <GcpStatCard
          label="Latest full month spend"
          value={formatUsd(latestTotal)}
          subtext={`${formatMonthLabel(latestMonth)} (complete month)`}
        />
        <GcpStatCard
          label="Current run rate → projected month"
          value={runRateProjection ? formatUsd(runRateProjection.runRateMonthly) : '—'}
          subtext={
            runRateProjection
              ? [
                  runRateProjection.runRateLabel,
                  runRateProjection.projectedMonth != null
                    ? `→ ~${formatUsd(runRateProjection.projectedMonth)} ${runRateProjection.projectedLabel ?? 'projected'}`
                    : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : mtd
                ? `→ ~${formatUsd(mtd.projected)} projected (${mtd.daysElapsed}/${mtd.daysInMonth} days)`
                : 'No run-rate data available'
          }
        />
        <GcpStatCard
          label="Month-over-month change"
          value={
            mom
              ? mom.change === 0
                ? '-'
                : mom.change > 0
                  ? `+${formatUsd(mom.change)}`
                  : formatUsd(mom.change)
              : '—'
          }
          subtext={
            mom
              ? `${mom.changePct >= 0 ? '+' : ''}${formatPct(mom.changePct)} vs ${formatMonthLabel(mom.previousMonth)} (complete months)`
              : undefined
          }
          valueClassName={mom ? (momPositive ? 'text-red-700' : 'text-green-700') : 'text-gray-900'}
        />
        <GcpStatCard
          label="Biggest change"
          value={
            biggestChange
              ? biggestChange.deltaUsd === 0
                ? '-'
                : `+${formatUsd(biggestChange.deltaUsd)}`
              : '—'
          }
          subtext={
            biggestChange
              ? [
                  biggestChange.service,
                  biggestChange.partition ? biggestChange.partition.replace(/_/g, ' ') : null,
                  biggestChange.onsetDate ? `onset ${biggestChange.onsetDate}` : null,
                ]
                  .filter(Boolean)
                  .join(' · ')
              : 'No spend-bridge deltas'
          }
          valueClassName={biggestChange && biggestChange.deltaUsd > 0 ? 'text-red-700' : 'text-gray-900'}
          onClick={biggestChange ? scrollToWhatChanged : undefined}
        />
      </div>

      {classification.total > 0 ? (
        <SpendClassificationBar classification={classification} />
      ) : null}

      {taxLabel && classification.tax_and_credits > 0 ? (
        <p className="text-sm text-gray-600">
          {taxLabel}: {formatUsd(classification.tax_and_credits)}/mo in tax & credits bucket
        </p>
      ) : null}

      <div className="grid gap-4 lg:grid-cols-2">
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-800">Monthly spend trend</h3>
          <MonthlyTrendChart rows={monthlyRows} />
        </div>
        <div>
          <h3 className="mb-2 text-sm font-medium text-gray-800">Daily spend (last 30 days)</h3>
          <DailySparkline dailyRows={dailyRows} anomalies={anomalies} />
        </div>
      </div>

      <div>
        <h3 className="mb-2 text-sm font-medium text-gray-800">Services</h3>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 text-sm">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-3 py-2 text-left font-medium text-gray-600">Service</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">Latest month</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">MoM change</th>
                <th className="px-3 py-2 text-right font-medium text-gray-600">% of total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {serviceRows.map((row) => (
                <tr
                  key={row.service}
                  className={row.anchorId ? 'cursor-pointer hover:bg-gray-50' : undefined}
                  onClick={() => row.anchorId && scrollToSection(row.anchorId)}
                >
                  <td className="px-3 py-2 font-medium text-gray-900">{row.service}</td>
                  <td className="px-3 py-2 text-right text-gray-800">{formatUsd(row.latestCost)}</td>
                  <td className="px-3 py-2 text-right text-gray-800">
                    {row.change != null
                      ? `${row.change >= 0 ? '+' : ''}${formatUsd(row.change)} (${row.changePct != null ? formatPct(row.changePct) : '—'})`
                      : '—'}
                  </td>
                  <td className="px-3 py-2 text-right text-gray-800">{formatPct(row.pctOfTotal)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {credits.length > 0 ? (
        <GcpCollapsible title="Credits applied" defaultOpen={false}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Month</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Type</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-600">Name</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-600">Amount</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {credits.map((row, index) => (
                  <tr key={`${row.month}-${row.credit_name}-${index}`}>
                    <td className="px-3 py-2 text-gray-800">{row.month}</td>
                    <td className="px-3 py-2 text-gray-800">{row.credit_type}</td>
                    <td className="px-3 py-2 text-gray-800">{row.credit_name}</td>
                    <td className="px-3 py-2 text-right text-gray-800">
                      {formatUsd(row.credit_amount)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </GcpCollapsible>
      ) : null}
    </div>
  )
}
